import React from 'react';
import {isFragment} from 'react-is';
import clsx from 'clsx';

import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {useArrowFocusItemAction} from '../../hooks/useArrowFocusItemAction';
import {
	getArrowFocusTrapItemActionsUtilityClass,
	ArrowFocusTrapItemActionsClasses,
	ARROW_FOCUS_TRAP_ITEM_ACTIONS_CLASS_NAME,
} from './arrowFocusTrapItemActionsClasses';

type ArrowFocusTrapItemActionsUtiltiyClassProps = WithClasses<
	ArrowFocusTrapItemActionsClasses,
	{}
>;

const useUtilityClasses = (
	ownerState: ArrowFocusTrapItemActionsUtiltiyClassProps,
) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(
		slots,
		getArrowFocusTrapItemActionsUtilityClass,
		classes,
	);
};

const ArrowFocusTrapItemActionsRoot = styled('div', {
	name: ARROW_FOCUS_TRAP_ITEM_ACTIONS_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
}));

export interface ArrowFocusTrapItemActionsProps
	extends React.DetailedHTMLProps<
			React.HTMLAttributes<HTMLDivElement>,
			HTMLDivElement
		>,
		WithClasses<
			ArrowFocusTrapItemActionsClasses,
			React.PropsWithChildren<{
				disabled?: boolean;
				//focusActive?: boolean;
				index: number;
			}>
		> {}

export const ArrowFocusTrapItemActions = ({
	classes: _classes,
	className,
	disabled,
	index,
	children: childrenProp,
	onClick,
	onMouseDown,
	...props
}: ArrowFocusTrapItemActionsProps) => {
	const {isActive, attributes} = useArrowFocusItemAction({index, disabled});
	const {tabIndex, handleMenuClose, handleMenuOpen, ...sendProps} = attributes;

	const classes = useUtilityClasses({
		classes: _classes,
	});

	const children = React.Children.map(childrenProp, (child) => {
		if (!React.isValidElement(child)) return null;

		if (process.env.NODE_ENV !== 'production') {
			if (isFragment(child)) {
				console.error(
					"The ArrowFocusTrapItemActions component doesn't accept a Fragment as a child.",
				);
			}
		}

		return React.cloneElement(child, {
			tabIndex,
			...child.props,
			TransitionProps: {
				...child.props?.TransitionProps,
				onEnter: handleMenuOpen(child.props?.TransitionProps?.onEnter),
				onExit: handleMenuClose(child.props?.TransitionProps?.onExit),
			},
		});
	});

	return (
		<ArrowFocusTrapItemActionsRoot
			{...props}
			{...sendProps}
			className={clsx(classes.root, className)}
			onMouseDown={(event) => {
				if (disabled) return;
				// prevents things like ListItemButton from showing animations
				// when clicking on actions/manus
				isActive && event.stopPropagation();
				onMouseDown?.(event);
			}}
		>
			{children}
		</ArrowFocusTrapItemActionsRoot>
	);
};

export default ArrowFocusTrapItemActions;
